import React,{useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiCloud, FiGlobe, FiDatabase, FiHardDrive, FiFolder, FiCpu, FiChevronDown, FiChevronUp, FiCheckCircle, FiExternalLink, FiTool
} = FiIcons;

const instructionsData={
  'Cloudflare Pages': {
    icon: FiCloud,
    color: 'orange',
    sections: {
      'Initial Setup': {
        steps: [
          'Sign up for a Cloudflare account at https://dash.cloudflare.com',
          'Go to the "Workers & Pages" section in the sidebar',
          'Click "Create application" and select the "Pages" tab',
          'Connect your GitHub account and select the repository to deploy'
        ]
      },
      'Build Configuration': {
        steps: [
          'For the framework preset, select "Vite"',
          'Build command should be `npm run build`',
          'Build output directory should be `dist`',
          'Add environment variables in Settings > Environment Variables'
        ]
      },
      'Custom Domain': {
        steps: [
          'After deployment, go to your project settings',
          'Click "Custom domains" to add your own domain',
          'Follow the instructions to update your DNS records',
          'Cloudflare will automatically provision an SSL certificate'
        ]
      }
    }
  },
  Netlify: {
    icon: FiGlobe,
    color: 'teal',
    sections: {
      'Initial Setup': {
        steps: [
          'Sign up for Netlify at https://app.netlify.com',
          'Click "Add new site" > "Import an existing project"',
          'Select GitHub as your provider and authorize access',
          'Choose the repository you want to deploy'
        ]
      },
      'Build Configuration': {
        steps: [
          'Build command should be `npm run build`',
          'Publish directory should be `dist`',
          'Set environment variables in Site settings > Build & deploy > Environment'
        ]
      },
      'Functions Setup': {
        steps: [
          'Create a `netlify/functions` directory in your project root',
          'Add JavaScript or TypeScript files with handler exports',
          'Your functions will be available at `/.netlify/functions/`',
          'Test locally using the Netlify CLI: `netlify dev`'
        ]
      }
    }
  },
  Supabase: {
    icon: FiDatabase,
    color: 'green',
    sections: {
      'Project Setup': {
        steps: [
          'Go to https://supabase.com and sign up or log in',
          'Click "New Project" and choose your organization',
          'Set a project name and a strong, secure database password',
          'Select a region closest to your users and wait for provisioning'
        ]
      },
      'Database Schema': {
        steps: [
          'Use the Table Editor to create your database tables',
          'Always enable Row Level Security (RLS) on new tables for security',
          'Create appropriate RLS policies for each table (SELECT, INSERT, UPDATE, DELETE)',
          'Add foreign key relationships and set default values for columns where applicable'
        ]
      },
      'Client Integration': {
        steps: [
          'Install the Supabase client library: `npm install @supabase/supabase-js`',
          'In your project settings, find your Project URL and anon key',
          'Add these as environment variables: `VITE_SUPABASE_URL` and `VITE_SUPABASE_ANON_KEY`',
          'Create a Supabase client instance in your application to interact with the database'
        ]
      }
    }
  },
  Dropbox: {
    icon: FiHardDrive,
    color: 'blue',
    sections: {
      'App Creation': {
        steps: [
          'Go to https://www.dropbox.com/developers and click "Create app"',
          'Choose "Scoped access" and select the level of access needed (e.g., "App folder")',
          'Name your app, agree to terms, and save your App key and App secret'
        ]
      },
      'OAuth Setup': {
        steps: [
          'In your app settings, go to the "Permissions" tab and add required scopes (e.g., `files.content.write`)',
          'In the "Settings" tab, add your application\'s redirect URIs',
          'Implement the OAuth 2.0 flow in your application to get user access tokens'
        ]
      }
    }
  },
  'Google Drive': {
    icon: FiFolder,
    color: 'yellow',
    sections: {
      'Project Setup': {
        steps: [
          'Go to the Google Cloud Console and create a new project',
          'Enable the Google Drive API for your project',
          'Go to "Credentials" and create an OAuth 2.0 Client ID',
          'Configure the OAuth consent screen with your app details'
        ]
      },
      'API Integration': {
        steps: [
          'Install the Google APIs client library: `npm install googleapis`',
          'Initialize the Drive API in your application using your credentials',
          'Implement file upload, download, and search functionality',
          'Handle authentication and token refresh logic carefully'
        ]
      }
    }
  },
  'Amazon AWS': {
    icon: FiCloud,
    color: 'gray',
    sections: {
      'S3 Bucket Setup': {
        steps: [
          'Sign in to the AWS S3 console and choose "Create bucket"',
          'Enter a unique, DNS-compliant name for your bucket',
          'Choose an AWS Region closest to your users',
          'Configure public access settings based on your needs'
        ]
      },
      'IAM User': {
        steps: [
          'Navigate to the IAM console and add a new user',
          'Select "Programmatic access" for the access type',
          'Attach a policy that grants necessary S3 permissions (e.g., `AmazonS3FullAccess`, but be more granular in production)',
          'Save the generated Access Key ID and Secret Access Key securely'
        ]
      }
    }
  },
  'Netlify Blobs': {
    icon: FiGlobe,
    color: 'teal',
    sections: {
      'Enabling Blobs': {
        steps: [
          'In your Netlify site dashboard, go to the "Integrations" tab',
          'Find "Netlify Blobs" and enable it for your site',
          'This will provision a blob store linked to your site'
        ]
      },
      'Usage in Code': {
        steps: [
          'Install the Netlify Blobs library: `npm install @netlify/blobs`',
          'Import and use `getStore` to interact with your blob store',
          'Use methods like `get`, `set`, and `delete` to manage your files',
          'Blobs can be accessed from Netlify Functions or Edge Functions'
        ]
      }
    }
  },
  'No Code Backend': {
    icon: FiDatabase,
    color: 'purple',
    sections: {
      'General Setup': {
        steps: [
          'Choose a No-Code backend provider (e.g., Firebase, Appwrite, Xano)',
          'Create a new project on your chosen platform',
          'Follow their documentation to set up your database collections/tables',
          'Configure authentication rules and API access policies'
        ]
      },
      'Frontend Integration': {
        steps: [
          'Install the provider\'s client-side SDK (e.g., `npm install firebase`)',
          'Initialize the client in your app using the API keys from your provider\'s dashboard',
          'Store your API keys securely in environment variables (e.g., `VITE_FIREBASE_API_KEY`)'
        ]
      }
    }
  },
  OpenAI: {
    icon: FiCpu,
    color: 'indigo',
    sections: {
      'API Key Setup': {
        steps: [
          'Visit platform.openai.com and sign up or log in',
          'Navigate to the "API Keys" section in your dashboard',
          'Click "Create new secret key"',
          'Copy the key and store it securely. You will not be able to see it again'
        ]
      },
      'Integration': {
        steps: [
          'Install the OpenAI SDK: `npm install openai`',
          'Set your API key as an environment variable (e.g., `VITE_OPENAI_API_KEY`)',
          'Initialize the OpenAI client in your code',
          'Use the `chat.completions.create` method to interact with models'
        ]
      }
    }
  },
  Anthropic: {
    icon: FiCpu,
    color: 'red',
    sections: {
      'API Key Setup': {
        steps: [
          'Visit console.anthropic.com and sign up or log in',
          'Navigate to "Account Settings" and then "API Keys"',
          'Click "Create Key", give it a name, and copy it securely'
        ]
      },
      'Integration': {
        steps: [
          'Install the Anthropic SDK: `npm install @anthropic-ai/sdk`',
          'Set your API key as an environment variable',
          'Initialize the Anthropic client',
          'Use the `messages.create` method to interact with Claude models'
        ]
      }
    }
  },
  'Google Gemini': {
    icon: FiCpu,
    color: 'blue',
    sections: {
      'API Key Setup': {
        steps: [
          'Visit aistudio.google.com and sign in',
          'Click "Get API key" and then "Create API key in new project"',
          'Copy your generated API key and store it safely'
        ]
      },
      'Integration': {
        steps: [
          'Install the Google Generative AI SDK: `npm install @google/generative-ai`',
          'Set your API key as an environment variable',
          'Initialize the `GoogleGenerativeAI` client',
          'Get a model instance and use `generateContent` to send prompts'
        ]
      }
    }
  },
  'Mistral AI': {
    icon: FiCpu,
    color: 'yellow',
    sections: {
      'API Key Setup': {
        steps: [
          'Visit console.mistral.ai and sign up',
          'Navigate to the "API Keys" section',
          'Click "Create new key", copy it, and store it securely'
        ]
      },
      'Integration': {
        steps: [
          'Install the Mistral SDK: `npm install @mistralai/mistralai`',
          'Set your API key as an environment variable',
          'Initialize the `MistralClient`',
          'Use the `chat` method to interact with Mistral models'
        ]
      }
    }
  }
};

const colorClasses={
  orange: {bg: 'bg-orange-500',text: 'text-orange-600',bgLight: 'bg-orange-100'},
  teal: {bg: 'bg-teal-500',text: 'text-teal-600',bgLight: 'bg-teal-100'},
  green: {bg: 'bg-green-500',text: 'text-green-600',bgLight: 'bg-green-100'},
  blue: {bg: 'bg-blue-500',text: 'text-blue-600',bgLight: 'bg-blue-100'},
  yellow: {bg: 'bg-yellow-500',text: 'text-yellow-600',bgLight: 'bg-yellow-100'},
  gray: {bg: 'bg-gray-500',text: 'text-gray-600',bgLight: 'bg-gray-100'},
  purple: {bg: 'bg-purple-500',text: 'text-purple-600',bgLight: 'bg-purple-100'},
  indigo: {bg: 'bg-indigo-500',text: 'text-indigo-600',bgLight: 'bg-indigo-100'},
  red: {bg: 'bg-red-500',text: 'text-red-600',bgLight: 'bg-red-100'}
};

const getPlatformName=key=> ({
  'cloudflare-pages': 'Cloudflare Pages',
  netlify: 'Netlify',
  supabase: 'Supabase',
  'no-code-backend': 'No Code Backend',
  dropbox: 'Dropbox',
  'netlify-blobs': 'Netlify Blobs',
  'google-drive': 'Google Drive',
  aws: 'Amazon AWS',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google Gemini',
  mistral: 'Mistral AI'
}[key] || key);

const Instructions=({backend,hosting,storage,aiProvider})=> {
  const platformKeys=[hosting,backend,storage,aiProvider];
  const availablePlatforms=Array.from(new Set(platformKeys.map(getPlatformName).filter(name=> instructionsData[name])));

  const [selectedPlatform,setSelectedPlatform]=useState(availablePlatforms[0] || 'Cloudflare Pages');
  const [expandedSections,setExpandedSections]=useState({});

  useEffect(()=> {
    const newAvailablePlatforms=Array.from(new Set(platformKeys.map(getPlatformName).filter(name=> instructionsData[name])));
    if (newAvailablePlatforms.length > 0 && !newAvailablePlatforms.includes(selectedPlatform)) {
      setSelectedPlatform(newAvailablePlatforms[0]);
    } else if (newAvailablePlatforms.length > 0 && selectedPlatform==='') {
      setSelectedPlatform(newAvailablePlatforms[0]);
    }
  },[backend,hosting,storage,aiProvider,selectedPlatform]);

  const toggleSection=(platform,section)=> {
    setExpandedSections(prev=> ({
      ...prev,
      [`${platform}-${section}`]: !prev[`${platform}-${section}`]
    }));
  };

  if (availablePlatforms.length===0) {
    return (
      <div className="text-center p-8 bg-slate-100 rounded-lg">
        <SafeIcon icon={FiTool} className="w-12 h-12 mx-auto text-slate-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-700">No Integrations Selected</h3>
        <p className="text-slate-500">Select an integration option above to see setup instructions.</p>
      </div>
    );
  }

  const currentPlatformData=instructionsData[selectedPlatform];
  if (!currentPlatformData) return null;

  const currentPlatformColors=colorClasses[currentPlatformData.color] || colorClasses.gray;

  return (
    <div className="w-full mx-auto">
      <div className="mb-8">
        <div className="flex flex-wrap justify-center gap-3">
          {availablePlatforms.map(platform=> {
            const platformData=instructionsData[platform];
            const platformColors=colorClasses[platformData.color] || colorClasses.gray;
            const Icon=platformData.icon;
            return (
              <button
                key={platform}
                onClick={()=> setSelectedPlatform(platform)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all transform ${
                  selectedPlatform===platform
                    ? `${platformColors.bg} text-white shadow-lg scale-105`
                    : 'bg-white border hover:shadow-md hover:scale-105'
                }`}
              >
                <SafeIcon icon={Icon} className="text-xl" />
                <span className="font-medium">{platform}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <SafeIcon icon={currentPlatformData.icon} className={`mr-2 ${currentPlatformColors.text}`} />
              {selectedPlatform}
            </h2>
            <div className="space-y-2">
              {Object.keys(currentPlatformData.sections).map(section=> (
                <button
                  key={section}
                  onClick={()=> toggleSection(selectedPlatform,section)}
                  className={`w-full text-left px-3 py-2 rounded transition-colors ${
                    expandedSections[`${selectedPlatform}-${section}`]
                      ? 'bg-blue-100 text-blue-700'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{section}</span>
                    <SafeIcon
                      icon={expandedSections[`${selectedPlatform}-${section}`] ? FiChevronUp : FiChevronDown}
                      className="text-xs"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {Object.entries(currentPlatformData.sections).map(([sectionTitle,section])=> (
            <div key={sectionTitle} className="bg-white rounded-lg shadow-md overflow-hidden">
              <button
                onClick={()=> toggleSection(selectedPlatform,sectionTitle)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50"
              >
                <h3 className="text-lg font-semibold">{sectionTitle}</h3>
                <SafeIcon icon={expandedSections[`${selectedPlatform}-${sectionTitle}`] ? FiChevronUp : FiChevronDown} />
              </button>
              <AnimatePresence>
                {expandedSections[`${selectedPlatform}-${sectionTitle}`] && (
                  <motion.div
                    initial={{height: 0,opacity: 0}}
                    animate={{height: 'auto',opacity: 1}}
                    exit={{height: 0,opacity: 0}}
                    transition={{duration: 0.3,ease: 'easeInOut'}}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 pt-2">
                      <ol className="space-y-4">
                        {section.steps.map((step,index)=> (
                          <li key={index} className="flex">
                            <span className={`flex-shrink-0 w-8 h-8 ${currentPlatformColors.bgLight} ${currentPlatformColors.text} rounded-full flex items-center justify-center text-sm font-semibold mr-4`}>
                              {index + 1}
                            </span>
                            <span className="text-gray-700 pt-1">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Instructions;